import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckSquare, Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { SectionHeader, EmptyState, StatusBadge, fmtDate } from "./ui";

const STATUSES = ["Not Started", "In Progress", "Waiting", "Completed", "Cancelled"];
const PRIORITIES = ["Low", "Medium", "High"];
const EMPTY = { name: "", description: "", client_id: "none", company_id: "none", category: "", due_date: "", priority: "Medium", status: "Not Started", client_visible: false, notes: "" };

export default function Tasks() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [sp, setSp] = useSearchParams();

  const load = useCallback(async () => {
    const [t, cl, co] = await Promise.all([api.get("/cc/tasks"), api.get("/cc/clients"), api.get("/cc/companies")]);
    setRows(t.data); setClients(cl.data); setCompanies(co.data); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (sp.get("new") === "1") { openNew(); sp.delete("new"); setSp(sp, { replace: true }); }
  }, [sp, setSp]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => {
    setEditId(t.id);
    setForm({ ...EMPTY, ...t, client_id: t.client_id || "none", company_id: t.company_id || "none", due_date: t.due_date || "", description: t.description || "", category: t.category || "", notes: t.notes || "" });
    setOpen(true);
  };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Task name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, client_id: form.client_id === "none" ? null : form.client_id, company_id: form.company_id === "none" ? null : form.company_id };
      delete payload.id; delete payload.client_name; delete payload.company_name; delete payload.created_at; delete payload.updated_at;
      if (editId) { await api.put(`/cc/tasks/${editId}`, payload); toast.success("Task updated"); }
      else { await api.post("/cc/tasks", payload); toast.success("Task created"); }
      setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete task "${t.name}"?`)) return;
    try { await api.delete(`/cc/tasks/${t.id}`); setRows((r) => r.filter((x) => x.id !== t.id)); toast.success("Task deleted"); }
    catch { toast.error("Failed to delete"); }
  };

  const updateStatus = async (t, status) => {
    try {
      await api.put(`/cc/tasks/${t.id}`, { ...t, status });
      setRows((r) => r.map((x) => x.id === t.id ? { ...x, status } : x));
    } catch { toast.error("Failed to update"); }
  };

  const shown = rows.filter((r) => statusFilter === "all" ? true : r.status === statusFilter);

  return (
    <div data-testid="tasks-page">
      <SectionHeader title="Tasks" subtitle="Everything to do across clients and companies."
        action={<Button data-testid="new-task-button" onClick={openNew} className="bg-primary text-primary-foreground hover:bg-[#DBC197] gap-1"><Plus size={18} weight="bold" /> New Task</Button>}
      />

      <div className="mb-4 w-48">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="task-status-filter" className="bg-zinc-900/60 border-white/10"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading ? <div className="text-muted-foreground">Loading…</div> :
        shown.length === 0 ? <EmptyState icon={CheckSquare} title="No tasks" hint="Create a task to track work and deadlines." /> : (
          <div className="rounded-xl border border-white/10 bg-zinc-900/50 divide-y divide-white/5" data-testid="tasks-list">
            {shown.map((t) => (
              <div key={t.id} data-testid={`task-row-${t.id}`} className="flex items-center justify-between gap-4 px-4 py-3.5">
                <div className="min-w-0">
                  <div className="font-medium truncate">{t.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {[t.client_name, t.company_name].filter(Boolean).join(" · ") || "Unassigned"} · due {fmtDate(t.due_date)}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusBadge status={t.priority} />
                  <Select value={t.status} onValueChange={(v) => updateStatus(t, v)}>
                    <SelectTrigger data-testid={`task-status-${t.id}`} className="h-8 w-36 bg-zinc-900 border-white/10 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="icon" variant="ghost" onClick={() => openEdit(t)} data-testid={`edit-task-${t.id}`}><PencilSimple size={16} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(t)} data-testid={`delete-task-${t.id}`} className="text-red-300 hover:text-red-200"><Trash size={16} /></Button>
                </div>
              </div>
            ))}
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 max-w-lg max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Task" : "New Task"}</DialogTitle><DialogDescription className="text-sm text-muted-foreground">Track work and deadlines across clients and companies.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label>Task name *</Label><Input data-testid="task-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Client</Label>
                <Select value={form.client_id} onValueChange={(v) => set("client_id", v)}>
                  <SelectTrigger data-testid="task-client-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name || c.business_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Company</Label>
                <Select value={form.company_id} onValueChange={(v) => set("company_id", v)}>
                  <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Category</Label><Input value={form.category} onChange={(e) => set("category", e.target.value)} /></div>
              <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} /></div>
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                  <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-white/10 p-3">
              <div className="text-sm font-medium">Visible to client</div>
              <Switch checked={form.client_visible} onCheckedChange={(v) => set("client_visible", v)} data-testid="task-visible-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-task-button" disabled={saving} onClick={save} className="bg-primary text-primary-foreground hover:bg-[#DBC197]">{saving ? "Saving…" : editId ? "Save changes" : "Create task"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
